import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from "@tanstack/react-query";
import Page from "@/components/Page";
import PageSkeleton from "@/components/PageSkeleton";
import { useEffect } from 'react';

/**
 * Router for the Policy Detail Page
 */

export const Route = createFileRoute('/policy/$pageId')({
  component: PolicyPage
});
function PolicyPage() {
  const {
    pageId
  } = Route.useParams();
  const hostname = window.location.hostname;
  const pageEndpoint = `https://${hostname}/wp-json/policy-library/v1/pages?page_id=${pageId}`;
  const {
    isPending: contentIsPending,
    error: contentError,
    data: contentData
  } = useQuery<PolicyPageData>({
    queryKey: ['confluence_page', pageId],
    queryFn: async () => {
      const res = await fetch(pageEndpoint);
      if (!res.ok) {
        throw new Error('Failed to fetch static content');
      }
      return res.json() as Promise<any>;
    },
    select: data => ({
      //Remove "startswith" labels since those are for the listing alpha filters,
      //then alphabetize remaining labels
      ...data,
      labels: {
        ...data.labels,
        results: data.labels.results.filter(label => !label.name.startsWith('startswith')).sort((a, b) => a.name.localeCompare(b.name))
      }
    })
  });
  useEffect(() => {
    if (contentData?.title) {
      document.title = `${contentData.title} - Princeton Theological Seminary`;
    }
  }, [contentData?.title]);
  return <>
        {contentIsPending && <PageSkeleton />}
        {!contentIsPending && contentError && <p className="error">This feature is currently experiencing a technical error. Please contact us if this issue persists.</p>}
        {!contentIsPending && !contentError && contentData && <Page contentData={contentData} />}
    </>;
}
const hot = import.meta.webpackHot;
if (hot && typeof window !== 'undefined') {
  ;
  hot.data ??= {};
  const tsrReactRefresh = window.__TSR_REACT_REFRESH__ ??= (() => {
    const ignoredExportsById = new Map();
    const previousGetIgnoredExports = window.__getReactRefreshIgnoredExports;
    window.__getReactRefreshIgnoredExports = ctx => {
      const ignoredExports = previousGetIgnoredExports?.(ctx) ?? [];
      const moduleIgnored = ignoredExportsById.get(ctx.id) ?? [];
      return [...ignoredExports, ...moduleIgnored];
    };
    return {
      ignoredExportsById
    };
  })();
  tsrReactRefresh.ignoredExportsById.set("/var/www/html/wp-content/mu-plugins/princeton-policy-library/policy-library-app/src/routes/policy/$pageId.tsx", ['Route']);
}
export function TSRFastRefreshAnchor() {
  return null;
}
if (import.meta.webpackHot) {
  const hot = import.meta.webpackHot;
  const hotData = hot.data ??= {};
  hot.accept(newModule => {
    if (Route && newModule && newModule.Route) {
      const routeId = hotData['tsr-route-id'] ?? Route.id;
      if (routeId) {
        hotData['tsr-route-id'] = routeId;
      }
      (function handleRouteUpdate(routeId, newRoute) {
        const router = window.__TSR_ROUTER__;
        const oldRoute = router.routesById[routeId];
        if (!oldRoute) return;
        const removedKeys = new Set();
        Object.keys(oldRoute.options).forEach(key => {
          if (!(key in newRoute.options)) {
            removedKeys.add(key);
            delete oldRoute.options[key];
          }
        });
        ["component", "shellComponent", "pendingComponent", "errorComponent", "notFoundComponent"].forEach(key => {
          if (key in oldRoute.options && key in newRoute.options) newRoute.options[key] = oldRoute.options[key];
        });
        oldRoute.options = newRoute.options;
        oldRoute.update(newRoute.options);
        oldRoute._componentsPromise = void 0;
        oldRoute._lazyPromise = void 0;
        router.routesById[oldRoute.id] = oldRoute;
        router.routesByPath[oldRoute.fullPath] = oldRoute;
        router.processedTree.matchCache.clear();
        router.processedTree.flatCache?.clear();
        router.processedTree.singleCache.clear();
        router.resolvePathCache.clear();
        walkReplaceSegmentTree(oldRoute, router.processedTree.segmentTree);
        const filter = m => m.routeId === oldRoute.id;
        const activeMatch = router.stores.matches.get().find(filter);
        const pendingMatch = router.stores.pendingMatches.get().find(filter);
        const cachedMatches = router.stores.cachedMatches.get().filter(filter);
        if (activeMatch || pendingMatch || cachedMatches.length > 0) {
          if (removedKeys.has("loader") || removedKeys.has("beforeLoad")) {
            const matchIds = [activeMatch?.id, pendingMatch?.id, ...cachedMatches.map(match => match.id)].filter(Boolean);
            router.batch(() => {
              for (const matchId of matchIds) {
                const store = router.stores.pendingMatchStores.get(matchId) || router.stores.matchStores.get(matchId) || router.stores.cachedMatchStores.get(matchId);
                if (store) store.set(prev => {
                  const next = {
                    ...prev
                  };
                  if (removedKeys.has("loader")) next.loaderData = void 0;
                  if (removedKeys.has("beforeLoad")) next.__beforeLoadContext = void 0;
                  return next;
                });
              }
            });
          }
          router.invalidate({
            filter,
            sync: true
          });
        }
        function walkReplaceSegmentTree(route, node) {
          if (node.route?.id === route.id) node.route = route;
          if (node.index) walkReplaceSegmentTree(route, node.index);
          node.static?.forEach(child => walkReplaceSegmentTree(route, child));
          node.staticInsensitive?.forEach(child => walkReplaceSegmentTree(route, child));
          node.dynamic?.forEach(child => walkReplaceSegmentTree(route, child));
          node.optional?.forEach(child => walkReplaceSegmentTree(route, child));
          node.wildcard?.forEach(child => walkReplaceSegmentTree(route, child));
        }
      })(routeId, newModule.Route);
    }
  });
}